/**
 * Shared sentiment-scale helpers (cream/teal token system).
 *
 * Single source of truth for mapping a sentiment score in [-1, +1] onto the
 * --tri-sent-* ramp, choosing readable text contrast, and formatting scores
 * for display. Extracted from OrgSentimentHeatmap so the per-interview and
 * per-session SentimentDashboard render identically to the org-level heatmap.
 */

/** Background color for a sentiment score on the --tri-sent-* ramp. */
export function getSentimentColor(score: number): string {
  if (score <= -0.6) return 'var(--tri-sent-n3)'; // very negative
  if (score <= -0.3) return 'var(--tri-sent-n2)'; // negative
  if (score < 0) return 'var(--tri-sent-n1)'; // slightly negative
  if (score === 0) return 'var(--tri-sent-0)'; // neutral/no data
  if (score < 0.3) return 'var(--tri-sent-p1)'; // slightly positive
  if (score < 0.6) return 'var(--tri-sent-p2)'; // positive
  return 'var(--tri-sent-p3)'; // very positive
}

/**
 * Text color for a sentiment score — the MANDATED ink for the fill that
 * getSentimentColor(score) returns (compass-tokens.css pairing table; every
 * pairing ≥ 4.5:1 by computed ratio).
 *
 * This banding MUST mirror getSentimentColor exactly: the old
 * `abs(score) > 0.3` shortcut disagreed with it at the ±0.30 boundaries,
 * putting dark ink on the n2/p2 fills (2.71:1 / 2.91:1 — fails AA).
 * Neutral and p1 are the only ink-text fills (on-teal on neutral is 2.50:1).
 */
export function getTextColor(score: number): string {
  if (score <= -0.6) return 'var(--tri-on-teal)'; // n3
  if (score <= -0.3) return 'var(--tri-on-teal)'; // n2
  if (score < 0) return 'var(--tri-on-teal)'; // n1
  if (score === 0) return 'var(--tri-ink)'; // neutral
  if (score < 0.3) return 'var(--tri-ink)'; // p1
  if (score < 0.6) return 'var(--tri-on-teal)'; // p2
  return 'var(--tri-on-teal)'; // p3
}

/**
 * Format a sentiment score for display: signed two decimals; 0 renders as
 * "0.00" (neutral EVIDENCE, on the model's own scoring contract). "No data"
 * is the absent-cell case — every caller renders its own "—" branch for that
 * (hasData / sampleSize guards), so the formatter must not conflate the two.
 */
export function formatScore(score: number): string {
  if (score === 0) return '0.00';
  return score > 0 ? `+${score.toFixed(2)}` : score.toFixed(2);
}

/** Abbreviate a persona name: initials for multi-word, prefix for single-word. */
export function getPersonaAbbrev(persona: string): string {
  const words = persona.split(' ');
  if (words.length === 1) return persona.slice(0, 4);
  return words.map(w => w[0]).join('').toUpperCase();
}
